<template>
  <div id="search-add-from-library-parent">
    <v-header :modal-button="true" @close-modal="cancelSearchAdd">
      <template #modal-title> {{ emittedTitle }} </template>
    </v-header>
    <v-stepper
      :labels="stepperLabels"
      :current-step="currentStep"
      class="search-add-stepper"
      @step-changed="handleStepChanged"
      unique-id="search-add-from-library-parent"
      :hide-not-visited="true"
      :key="stepperKey"
    ></v-stepper>
    <SearchAddFromLibrary
      v-if="currentStep === 1"
      :material-type-label="materialTypeLabel"
      :group-nid="groupNid"
      :material-type="materialType"
      @cancel-search-add="cancelSearchAdd"
      @change-title="changeTitle"
      @material-added="materialAddedCallback"
      @back="handleBack"
    />
    <BioMaterialStepChooser
      v-if="currentStep === 2 && selectedMaterialUuid"
      @bio-material-step-chooser-selection="handleStepChooserSelection($event)"
      @choose-modification="chooseModification"
      :group-nid="groupNid"
      :material-type="materialType"
      :uuid="selectedMaterialUuid"
      @change-title="changeTitle"
    />
    <BioMaterialStepModify
      v-if="currentStep === 3 && selectedMaterialUuid"
      :group-nid="groupNid"
      :material-type="materialType"
      :uuid="selectedMaterialUuid"
      :key="currentStep"
      @choose-modification="chooseModification"
      @emit-step-change="handleStepChanged"
      @emit-close-modal="cancelSearchAdd"
      @handle-form-data="handleModifyFormData"
      @change-title="changeTitle"
    />
    <BioMaterialStepSummary
      v-if="currentStep === 4 && selectedMaterialUuid"
      :material-label="modifiedMaterialLabel"
      :group-nid="groupNid"
      :material-removed="materialRemoved"
      :success-status="successStatus"
      @summary-status="handleSummaryStatus"
      @change-title="changeTitle"
    />
    <VEmptyState
      v-if="showEmptyState"
      :title="emptyStateTitle"
      :description="emptyStateDescription"
      :use-without-v-html="true"
    />
  </div>
</template>

<script>
import VEmptyState from "@/components/raft/VEmptyState.vue";
import { restClient } from "@/lib/raftApi";
import VHeader from "../raft/VHeader.vue";
import VStepper from "../raft/VStepper.vue";
import SearchAddFromLibrary from "./SearchAddFromLibrary.vue";
import BioMaterialStepChooser from "./BioMaterialStepChooser.vue";
import BioMaterialStepModify from "./BioMaterialStepModify.vue";
import BioMaterialStepSummary from "./BioMaterialStepSummary.vue";

export default {
  name: "SearchAddFromLibraryParent",
  data() {
    return {
      emittedTitle: "Add Material",
      added: false,
      modified: false,
      currentStep: 1,
      stepperLabels: ["Search", "Add", "Modify", "Summary"],
      parentData: {},
      stepperKey: 0,
      emptyStateTitle: "Looks like you missed a step!",
      emptyStateDescription:
        "There are additional steps to complete this process.",
      uuidFromSearchStep: null,
      materialRemoved: false,
      copyMaterialToGroupResponseData: {},
      groupQueries: {},
      selectedMaterialUuid: "",
      selectedMaterialData: {},
    };
  },
  props: {
    materialType: { type: String, required: true },
    groupNid: { type: String, required: true },
    materialTypeLabel: { type: String, required: true },
  },
  components: {
    VHeader,
    VStepper,
    SearchAddFromLibrary,
    BioMaterialStepChooser,
    BioMaterialStepSummary,
    BioMaterialStepModify,
    VEmptyState,
  },
  methods: {
    chooseModification(e) {
      this.modified = e;
    },
    handleStepChanged(e) {
      // e are step numbers = 1, 2, 3, 4
      this.$set(this, "currentStep", e);
    },
    updateParentData({ data, nextStep }) {
      this.$set(this, "parentData", data);
      this.handleStepChanged(nextStep);
    },
    async handleStepChooserSelection(value) {
      await this.copyMaterialToGroup(
        this.selectedMaterialData.drupal_internal__id,
      );

      if (value === "modify") {
        // User wants to edit the material, so move to the next step.
        this.handleStepChanged(3);
      } else {
        // User wants to add the material as is, so move to the summary step.
        // Create the actual material here.
        // Move to step 4.
        this.handleStepChanged(4);
        this.added = true;
        this.$showNotification(
          "success",
          "Material has been added without modifying.",
        );
      }
    },
    startNewForm() {
      this.stepperKey += 1;
      this.$set(this, "currentStep", 1);
      this.$set(this, "parentData", {});
      this.$set(this, "uuidFromSearchStep", null);
      this.$set(this, "materialRemoved", false);
    },
    handleModifyFormData(e) {
      // If Modify step involved Removed Material
      if (e.includes("Removed")) {
        this.materialRemoved = true;
      }
    },
    cancelSearchAdd() {
      this.emitToParentModal("cancel-search-add");
    },
    emitAssociateMaterials() {
      this.emitToParentModal("associate-materials");
    },
    handleSummaryStatus({ status }) {
      if (status === "done") {
        this.cancelSearchAdd();
      }
      if (status === "add-another") {
        this.startNewForm();
      }
      if (status === "associate-materials") {
        this.emitAssociateMaterials();
      }
      if (status === "back") {
        this.emitToParentModal("back");
      }
    },
    emitToParentModal(status) {
      this.$emit("parent-status", { status });
    },
    materialAddedCallback({
      selectedBioMaterial,
      selectedBioMaterialData,
      groupQueries,
    }) {
      this.selectedMaterialUuid = selectedBioMaterial;
      this.selectedMaterialData = selectedBioMaterialData;
      this.groupQueries = groupQueries;
      this.updateParentData({
        data: this.selectedMaterialData,
        nextStep: 2,
      });
    },
    async copyMaterialToGroup(bioMaterialId) {
      if (bioMaterialId !== "") {
        const path = `/${this.groupNid}/bio-materials/library-to-group/${bioMaterialId}`;
        const queries = this.groupQueries;
        this.copyMaterialToGroupResponseData = await restClient
          .post({ path, queries, data: {} })
          .catch(({ message, data }) => {
            this.errorEmit(message, data);
            this.$showNotification(
              "error",
              `Problem with adding material. ${message}.`,
            );
          });
        try {
          if (this.copyMaterialToGroupResponseData) {
            this.selectedMaterialUuid =
              this.copyMaterialToGroupResponseData.uuid[0].value;
          }
        } catch ({ message, data }) {
          this.errorEmit(message, data);
        }
      }
    },
    errorEmit(message, data) {
      this.$showNotification("error", `Form Error. ${message}.`);
      this.$emit("form-error-message", { message: message, data: data });
    },
    changeTitle(title) {
      this.emittedTitle = title;
    },
    handleBack() {
      this.emitToParentModal("back");
    },
  },
  computed: {
    materialLabel() {
      const parentName =
        this.parentData?.name?.[0]?.value ||
        this.parentData?.field_name?.[0]?.value;
      if (parentName) {
        return parentName;
      }
      const name =
        this.selectedMaterialData?.name ||
        this.selectedMaterialData?.field_name;
      return name || "Material";
    },
    modifiedMaterialLabel() {
      return this.modified
        ? `modified ${this.materialLabel}`
        : this.materialLabel;
    },
    showEmptyState() {
      return this.currentStep !== 1 && !this.selectedMaterialUuid;
    },
    successStatus() {
      if (this.materialRemoved) {
        return "Removed";
      }
      if (this.added) {
        return "Success";
      }
      return "Summary";
    },
  },
};
</script>

<style scoped>
:deep(.modal-content) {
  padding: 0.25rem 0.75rem;
}

:deep(.modal-body) {
  padding: 0;
}

#search-add-from-library-parent {
  display: grid;
  grid-template-rows: max-content max-content 1fr;
}

:deep(.raft-bio-mat-search-result-actions) {
  margin-top: auto;
}
</style>
